import { useState, useEffect, useMemo } from 'react';
import { storage } from '@/utils/utils';
import { useSelector } from 'umi';

interface IUseRedDotProps {
  showRedDot?: boolean;
  storageKey: string;
  pageId: string;
  buttonId: string;
}

/**
 * 红点状态管理 Hook
 *
 * 用于管理按钮红点的显示和隐藏状态，支持以下功能：
 * - 持久化红点状态到 localStorage
 *
 * @param showRedDot - 是否显示红点，默认 false
 * @param storageKey - 存储键名，用于生成唯一的 localStorage key
 * @param pageId - 页面ID，用于区分不同页面的红点状态
 * @param buttonId - 按钮ID，用于区分同一页面不同按钮的红点状态
 *
 * @returns 返回红点状态管理对象
 * @returns {boolean} isInStorage - 红点状态是否已存储在 localStorage 中
 * @returns {boolean} shouldShowRedDot - 是否应该显示红点
 * @returns {Function} handleRedDotDisappear - 处理红点消失的回调函数
 *
 * @example
 * ```tsx
 * const { shouldShowRedDot, handleRedDotDisappear } = useRedDot({
 *   showRedDot: true,
 *   storageKey: '__dbd_vs_benchmark_red_dot__',
 *   pageId: 'dashboard_overview',
 *   buttonId: 'vs_benchmark_001'
 * });
 * ```
 */
export const useRedDot = ({
  showRedDot = false,
  storageKey,
  pageId,
  buttonId,
}: IUseRedDotProps) => {
  // 默认改为 true 防止红点在读取之前 storage 之前显示
  const [isInStorage, setIsInStorage] = useState<boolean>(true);

  // 从 Redux 状态获取用户信息
  const { userInfo, currentGame } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
    currentGame: state?.dashboard?.currentGame,
  }));

  const userId = userInfo?.userid;
  const gameCode = currentGame?.game_code;

  // 生成唯一的存储键，结合 userId 和 buttonId
  const uniqueStorageKey = useMemo(() => {
    const baseKey = storageKey.endsWith('__') ? storageKey : `${storageKey}__`;
    return baseKey.replace(
      /__$/,
      `_${userId || 'unknown_user'}_${gameCode || 'unknown_game_code'}_${pageId || 'unknown_page'}_${buttonId || 'unknown_button'}__`,
    );
  }, [storageKey, userId, pageId, buttonId, gameCode]);

  // 检查用户是否已 hover 过
  useEffect(() => {
    if (showRedDot) {
      const inStorage = storage.getLocalStorageItem(uniqueStorageKey);
      setIsInStorage(!!inStorage);
    }
  }, [showRedDot, uniqueStorageKey]);

  // 处理红点消失的逻辑
  const handleRedDotDisappear = (): void => {
    // 提前返回，避免重复操作
    if (!showRedDot || isInStorage) return;

    try {
      storage.setLocalStorageItem(uniqueStorageKey, true);
      setIsInStorage(true);
    } catch (error) {
      console.error('Failed to save red dot state:', error);
    }
  };

  return {
    isInStorage,
    shouldShowRedDot: showRedDot && !isInStorage,
    handleRedDotDisappear,
  };
};
